import { useState } from 'react';
import {
  SimpleGrid, Card, Text, Group, Button, Table, Badge, Select, Modal,
  Box, ThemeIcon, ScrollArea, Center, TextInput, Divider,
} from '@mantine/core';
import { MonthPickerInput, DatePickerInput } from '@mantine/dates';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import {
  IconPlus, IconCash, IconArrowDownRight, IconArrowUpRight, IconScale, IconTrash,
  IconAlertTriangle, IconChevronRight, IconSearch, IconInfoCircle, IconTags,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { PageHeader } from '../components/PageHeader';
import { StatCard } from '../components/StatCard';
import { TableToolbar } from '../components/TableToolbar';
import { TableSkeleton } from '../components/TableSkeleton';
import { FetchOverlay } from '../components/FetchOverlay';
import { EmptyState } from '../components/EmptyState';
import { ExpenseCategoriesModal, type ExpenseType } from '../components/ExpenseCategoriesModal';
import { ExpenseFormModal } from '../components/ExpenseFormModal';
import { SectionCard } from '../components/SectionCard';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete } from '../lib/confirm';
import { formatSom, formatDate, formatDateTime } from '../lib/format';
import { monthLabel as fmtMonth, monthToDate, thisMonth, ym } from '../lib/month';

const CATEGORIES = ['SALARY', 'RENT', 'UTILITY', 'SUPPLY', 'TAX', 'MAINTENANCE', 'OTHER'] as const;
type Category = (typeof CATEGORIES)[number];
const CAT_COLOR: Record<Category, string> = {
  SALARY: 'blue', RENT: 'grape', UTILITY: 'teal', SUPPLY: 'orange', TAX: 'red', MAINTENANCE: 'cyan', OTHER: 'gray',
};
/** Xarajat turlarini YARATISH/TAHRIRLASH huquqi (backend `STAFF` bilan bir xil).
 *  Buxgalter turlardan foydalanadi, lekin ro'yxatni o'zgartira olmaydi. */
const TYPE_EDITOR_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN'];

interface Expense {
  id: string; category: Category; amount: number; note?: string | null;
  spentAt: string; forMonth?: string | null;
  /** Qaysi puldan: CASH — naqd kassa, qolganlari — bank. Eski qatorlarda CASH. */
  method?: string;
  /** T11 — xarajat turi (null: turlar joriy etilishidan oldingi ESKI qator) */
  categoryId?: string | null;
  categoryRef?: { id: string; name: string; legacyCategory: Category } | null;
  user?: { id: string; fullName: string; role: string } | null;
  createdBy?: { id: string; fullName: string } | null;
}
interface Finance {
  month: string; income: number; cashIncome?: number; expenses: number; net: number;
  expensesByCategory: { category: Category; total: number }[];
  debtorsCount: number; debtTotal: number;
}
/** GET /expenses/summary — ro'yxat bilan AYNI filtrlar bo'yicha taqsimotlar */
interface ExpenseSummary {
  month: string | null; total: number;
  byCategory: { category: Category; total: number }[];
  /** T11 — turlar bo'yicha taqsimot; `categoryId: null` — turga bog'lanmagan eski qatorlar */
  byType?: { categoryId: string | null; name: string | null; legacyCategory: Category | null; isActive: boolean; total: number }[];
}

/** Chip / detal modalining manbai: eski enum kategoriya yoki T11 turi. */
type DetailSel = { kind: 'enum'; value: Category } | { kind: 'type'; id: string; name: string; color: string };

export default function Expenses() {
  const { t } = useTranslation();
  // To'lov usullari — Moliya (to'lovlar) sahifasi bilan AYNI yorliqlar
  const METHODS = [
    { value: 'CASH', label: t('paymentsPage.methodCash') },
    { value: 'CARD', label: t('paymentsPage.methodCard') },
    { value: 'TRANSFER', label: t('paymentsPage.methodTransfer') },
    { value: 'OTHER', label: t('paymentsPage.methodOther') },
  ];
  const methodLabel = (m?: string) => METHODS.find((x) => x.value === (m ?? 'CASH'))?.label ?? m;
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEditTypes = TYPE_EDITOR_ROLES.includes(user?.role ?? '');
  const [opened, { open, close }] = useDisclosure(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [month, setMonth] = useState<string>(thisMonth());
  const [catF, setCatF] = useState<string>('');
  const [typeF, setTypeF] = useState<string>('');
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [sort, setSort] = useState('spentAt:desc');
  // Oxirgi tanlangan oy — sana oralig'i tozalanganda AYNI shu oyga qaytiladi (joriy oyga
  // emas): foydalanuvchi ko'rib turgan oy jimgina almashtirilmasin (R11).
  const [lastMonth, setLastMonth] = useState<string>(thisMonth());

  const catLabel = (c: Category) => t(`expenseCat.${c}`);
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  /**
   * "YYYY-MM" → tarjima qilingan yorliq ("Avg 2026") — ekranda mashina formati chiqmasin.
   * T9: qiymat avval NORMALLASHTIRILADI (`lib/month`), shu tufayli bazadagi eski
   * "2026-5" ham "2026-05" bilan bir xil ko'rinadi.
   */
  const monthLabel = (m?: string | null) => fmtMonth(m, monthNames);

  // Lokal sana → "YYYY-MM-DD". toISOString() EMAS — UTC+5 da kun bir kun orqaga siljirdi.
  const iso = (d: Date | null) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

  // Oy va sana oralig'i — O'ZARO INKOR filtrlar (F10).
  // `month` xarajat QAYSI OYGA tegishli ekani (forMonth), `range` esa QACHON sarflangani
  // (spentAt) bo'yicha ishlaydi; backend ularni AND bilan biriktiradi. Ikkalasi birga
  // tanlanganda kesishma ko'pincha bo'sh chiqib, ekranda sababsiz "ma'lumot yo'q" ko'rinardi.
  // Shuning uchun biri tanlanganda ikkinchisi tozalanadi, nima bo'lganini foydalanuvchi
  // ko'rishi uchun esa ro'yxat tepasida izoh va "oyga qaytish" tugmasi chiqadi.
  const rangeActive = !!(range[0] || range[1]);

  // Ro'yxat va kategoriya chiplari AYNI filtrlarni ishlatadi — aks holda chipdagi
  // summa jadvaldagi qatorlarga mos kelmay qolardi. (`sort` bu obyektga kirmaydi.)
  const filters = {
    month: month || undefined,
    category: catF || undefined,
    // T11 — aniq TUR bo'yicha filtr (eski enum filtri turlar bo'lmaganda ishlaydi)
    categoryId: typeF || undefined,
    q: qDeb || undefined,
    from: iso(range[0]),
    to: iso(range[1]),
  };
  // Chiplar uchun kategoriya filtri OLIB TASHLANADI: "Kategoriya bo'yicha taqsimot"
  // kartasi kategoriya tanlanganda bitta chipga qisqarib qolsa, boshqa kategoriya
  // detalini ochish (chipga bosish) imkoni yo'qoladi — taqsimot ma'nosini yo'qotadi.
  // Oy / sana oralig'i / qidiruv esa jadval bilan bir xil qoladi.
  const summaryFilters = { ...filters, category: undefined, categoryId: undefined };
  // Bo'sh holat matni uchun UCH holat ajratiladi (R34):
  //   1) toraytiruvchi filtr faol  → "Filtrga mos xarajat yo'q";
  //   2) filtrsiz, oy tanlangan    → "Xarajatlar yo'q" + QAYSI oy bo'sh ekani;
  //   3) filtrsiz, "barcha oylar"  → "Xarajatlar yo'q" (bazada umuman yozuv yo'q).
  // Oy tanlovi filtr HISOBLANMAYDI: sahifa doim biror oy bilan ochiladi, shuning uchun oy
  // tanlangani uchun "filtrga mos yo'q" desak, hali xarajat kiritmagan maktab bazada yozuv
  // bor deb o'ylardi (R13). Ilgari bu shartga `month !== thisMonth()` ham kirar edi va aynan
  // kerakli holatda — boshqa oy tanlanib, o'sha oy bo'sh chiqqanda — "qaysi oy bo'sh" izohini
  // o'chirib yuborardi (R34).
  const narrowFilter = !!(catF || typeF || qDeb || rangeActive);

  // Yuqoridagi moliya kartalari (kirim/chiqim/sof/qarzdorlik) — har doim BITTA oy kesimi:
  // backend `/reports/finance` faqat `month` qabul qiladi (sana oralig'i bo'yicha kirim va
  // qarzdorlikni hisoblay olmaydi). Oy filtri tozalanganda kartalar oraliq boshlangan oyni,
  // u ham bo'lmasa joriy oyni ko'rsatadi.
  const statMonth = month || (range[0] ? ym(range[0]) : thisMonth());
  // Kartalar davri jadval davridan farq qilsa — buni ochiq yozamiz, aks holda raqamlar
  // pastdagi yig'indiga sababsiz mos kelmagandek ko'rinardi (R9).
  const statScopeDiffers = month !== statMonth;
  // `/reports/finance` FAQAT `month` ni qabul qiladi (backend `routes/reports.ts`) — qidiruv
  // ham, kategoriya filtri ham unga umuman uzatilmaydi, jadval va kategoriya chiplari esa
  // ikkalasini ham qo'llaydi. Yolg'iz "davr" yorlig'i bu nomuvofiqlikni YASHIRARDI ("davr
  // bir xil-ku"), shuning uchun kartalar nimani hisobga olmaganini ochiq yozamiz (R33).
  const statIgnoresFilters = !!(qDeb || catF || typeF);

  const { data: fin } = useQuery({
    queryKey: ['finance', statMonth],
    queryFn: async () => (await api.get<Finance>('/reports/finance', { params: { month: statMonth } })).data,
  });

  const { data: sum } = useQuery({
    queryKey: ['expenses-summary', summaryFilters],
    queryFn: async () => (await api.get<ExpenseSummary>('/expenses/summary', { params: summaryFilters })).data,
  });

  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = { ...filters, sort };
  const { items, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Expense>(
    'expenses', '/expenses', filtrlar, 12,
  );

  // ─── T11: xarajat turlari ────────────────────────────────────────────────
  // Ro'yxat TO'LIQ olinadi (nofaollari ham): eski xarajat allaqachon nofaol turga
  // bog'langan bo'lishi mumkin va uning nomi jadvalda ko'rinishi kerak. Formada
  // (`ExpenseFormModal`) esa faqat FAOL turlar taklif qilinadi.
  const { data: types } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => (await api.get<ExpenseType[]>('/expenses/categories')).data,
    staleTime: 60_000,
  });
  const typeList = types ?? [];
  // Turlar hali yaratilmagan (seed skripti ishlamagan) bo'lsa — eski enum rejimi
  // saqlanadi, aks holda admin umuman xarajat qo'sha olmay qolardi.
  const useTypes = typeList.length > 0;

  // ─── "Kategoriya bo'yicha" chipiga bosilganda ochiladigan detal ro'yxati ──
  const [detailSel, setDetailSel] = useState<DetailSel | null>(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailQ] = useDebouncedValue(detailSearch, 300);
  // Detal — chip QAYSI kesimdan ochilgan bo'lsa, AYNI o'sha kesim (oy + sana oralig'i +
  // toolbar qidiruvi). Faqat `month` ishlatilsa chipdagi summa modaldagi qatorlarga
  // mos kelmay qolardi. Modaldagi qidiruv — shu kesim ICHIDA yana toraytirish.
  const detail = usePagedQuery<Expense>(
    'expense-detail',
    '/expenses',
    {
      ...summaryFilters,
      category: detailSel?.kind === 'enum' ? detailSel.value : undefined,
      categoryId: detailSel?.kind === 'type' ? detailSel.id : undefined,
      q: detailQ || summaryFilters.q,
    },
    10,
    !!detailSel, // modal yopiq bo'lsa so'rov yuborilmaydi
  );
  const openDetail = (sel: DetailSel) => { setDetailSearch(''); setDetailSel(sel); };
  const closeDetail = () => setDetailSel(null);
  // Kategoriya/tur jami — bosilgan chipdagi AYNI qiymat (modal qidiruvidan mustaqil).
  const detailTotal = !detailSel
    ? 0
    : detailSel.kind === 'enum'
      ? (sum?.byCategory.find((c) => c.category === detailSel.value)?.total ?? 0)
      : (sum?.byType?.find((c) => c.categoryId === detailSel.id)?.total ?? 0);

  // Oy tanlansa sana oralig'i, oraliq tanlansa oy tozalanadi (yuqoridagi F10 izohiga qarang).
  const pickMonth = (d: Date | null) => {
    const nm = d ? ym(d) : '';
    setMonth(nm);
    setLastMonth(nm); // "barcha oylar" tanlovi ham eslab qolinadi
    setRange([null, null]);
    setPage(1);
  };
  const pickRange = (v: [Date | null, Date | null]) => {
    setRange(v);
    // Oraliq tanlansa oy olib qo'yiladi; oraliq tozalansa (jumladan DatePickerInput'ning
    // O'Z X tugmasi bilan) oy filtri TIKLANADI — aks holda sahifa izohsiz "barcha oylar"
    // rejimida qolib ketardi (R10).
    setMonth(v[0] || v[1] ? '' : lastMonth);
    setPage(1);
  };
  // Izohdagi tugma AYNI `pickRange([null, null])` yo'lidan o'tadi — ya'ni DatePickerInput'ning
  // O'Z X tugmasi bilan bir xil natija beradi (R35). Ilgari tugma `lastMonth || thisMonth()`
  // ishlatgani uchun "barcha oylar" tanlab qo'yilgan holatda tugma joriy oyga, X esa "barcha
  // oylar"ga qaytarardi — bitta holatdan ikki xil natija. Endi ikkalasi ham foydalanuvchi
  // oxirgi tanlagan holatni tiklaydi ("barcha oylar" ham tanlov — jimgina almashtirilmaydi).
  const clearRange = () => pickRange([null, null]);

  // Davr yorlig'i (kartalar va modal sarlavhasi uchun) — qaysi filtr faol bo'lsa o'sha.
  // Yarim tanlangan oraliqda "01.07.2026 – —" o'rniga ma'noli matn beriladi (R14).
  const rangeLabel = () => {
    const [a, b] = range;
    if (a && b) return `${formatDate(a)} – ${formatDate(b)}`;
    if (a) return t('expenses.rangeFrom', { date: formatDate(a) });
    if (b) return t('expenses.rangeTo', { date: formatDate(b) });
    return '';
  };
  const periodLabel = month ? monthLabel(month)
    : rangeActive ? rangeLabel() : t('expenses.allMonths');

  // Kartalar jadvaldan QAYERDA farq qilishi — bitta qatorda, ochiq: davr farqi (R9) va
  // kartalarga umuman qo'llanmagan filtrlar (R33). Ikkalasi ham bo'lmasa qator jim turadi.
  const statNotes = [
    statScopeDiffers ? t('expenses.statsPeriodDiffers', { period: periodLabel }) : '',
    statIgnoresFilters ? t('expenses.statsIgnoresFilters') : '',
  ].filter(Boolean);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['expense-detail'] });
    qc.invalidateQueries({ queryKey: ['expenses-summary'] });
    qc.invalidateQueries({ queryKey: ['finance'] });
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/expenses/${id}`),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        title={t('pages.expenses.title')}
        action={
          <Group gap="sm">
            {/* T11 — turlarni boshqarish faqat administratsiyada (buxgalter faqat ishlatadi) */}
            {canEditTypes && (
              <Button variant="light" leftSection={<IconTags size={18} />} onClick={() => setTypesOpen(true)}>
                {t('expenseTypes.manage')}
              </Button>
            )}
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={open}
            >
              {t('expenses.add')}
            </Button>
          </Group>
        }
      />

      {/* Kirim / Chiqim / Sof / Qarzdorlik — oy kesimi (jadval filtridan mustaqil).
          Jadval boshqa davrni ko'rsatayotgan bo'lsa, farq shu yerda ochiq aytiladi. */}
      <Group gap={6} mb={6} align="center">
        <Text fz="xs" c="dimmed">{t('expenses.statsMonth', { month: monthLabel(statMonth) })}</Text>
        {statNotes.length > 0 && (
          <>
            <IconInfoCircle size={14} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
            <Text fz="xs" c="orange.7">{statNotes.join(' · ')}</Text>
          </>
        )}
      </Group>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
        <StatCard icon={IconArrowUpRight} color="green" label={t('expenses.income')} value={formatSom(fin?.income ?? 0)}
          hint={fin?.cashIncome != null ? t('expenses.cashIncomeHint', { sum: formatSom(fin.cashIncome) }) : undefined} />
        <StatCard icon={IconArrowDownRight} color="red" label={t('expenses.expenses')} value={formatSom(fin?.expenses ?? 0)} />
        <StatCard icon={IconScale} color={fin && fin.net < 0 ? 'red' : 'teal'} label={t('expenses.net')} value={formatSom(fin?.net ?? 0)} />
        <StatCard icon={IconAlertTriangle} color="orange" label={t('expenses.debt')} value={formatSom(fin?.debtTotal ?? 0)}
          hint={`${fin?.debtorsCount ?? 0} ${t('expenses.debtors')}`} />
      </SimpleGrid>

      {/* Taqsimot — jadval bilan AYNI filtrlar bo'yicha.
          T11: turlar mavjud bo'lsa TURLAR kesimi ko'rsatiladi (foydalanuvchi o'zi
          yaratgan nomlar), aks holda eski enum kategoriyalari. */}
      {sum?.byCategory.some((c) => c.total > 0) && (
        <Card mb="md">
          <Group justify="space-between" mb="sm" wrap="nowrap">
            {/* Davr yorlig'i shart: bu karta jadval filtri bo'yicha hisoblanadi va
                yuqoridagi "Chiqim" kartasidan BOSHQA davrni ko'rsatishi mumkin (R12). */}
            <Group gap={8} wrap="nowrap" align="baseline">
              <Text fw={700}>{useTypes ? t('expenseTypes.byType') : t('expenses.byCategory')}</Text>
              <Text fz="xs" c="dimmed">{periodLabel}</Text>
            </Group>
            <Text fz="xs" c="dimmed">{t('expenses.clickHint')}</Text>
          </Group>
          {/* Chip'lar — haqiqiy tugma: hover/fokus va kursor Mantine'ning o'zidan keladi */}
          <Group gap="sm">
            {useTypes
              ? (sum.byType ?? []).filter((c) => c.total > 0).map((c) => {
                  const color = CAT_COLOR[c.legacyCategory ?? 'OTHER'];
                  // `categoryId: null` — turga bog'lanmagan ESKI qatorlar. Ular uchun
                  // backendda filtr yo'q, shuning uchun chip bosilmaydi (faqat summa).
                  if (!c.categoryId) {
                    return (
                      <Badge key="no-type" variant="outline" color="gray" radius="xl" size="lg">
                        {t('expenses.noType')}: {formatSom(c.total)}
                      </Badge>
                    );
                  }
                  return (
                    <Button
                      key={c.categoryId}
                      variant="light"
                      color={color}
                      radius="xl"
                      size="compact-md"
                      onClick={() => openDetail({ kind: 'type', id: c.categoryId!, name: c.name ?? '', color })}
                      leftSection={<Box w={8} h={8} style={{ borderRadius: 4, background: `var(--mantine-color-${color}-6)` }} />}
                      rightSection={<IconChevronRight size={14} />}
                    >
                      {c.name}: {formatSom(c.total)}
                    </Button>
                  );
                })
              : sum.byCategory.filter((c) => c.total > 0).map((c) => (
                  <Button
                    key={c.category}
                    variant="light"
                    color={CAT_COLOR[c.category]}
                    radius="xl"
                    size="compact-md"
                    onClick={() => openDetail({ kind: 'enum', value: c.category })}
                    leftSection={<Box w={8} h={8} style={{ borderRadius: 4, background: `var(--mantine-color-${CAT_COLOR[c.category]}-6)` }} />}
                    rightSection={<IconChevronRight size={14} />}
                  >
                    {catLabel(c.category)}: {formatSom(c.total)}
                  </Button>
                ))}
          </Group>
        </Card>
      )}

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('expenses.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'spentAt:desc', label: t('att.sortDateDesc') },
          { value: 'spentAt:asc', label: t('att.sortDateAsc') },
          { value: 'amount:desc', label: t('paymentsPage.sortAmountDesc') },
          { value: 'amount:asc', label: t('paymentsPage.sortAmountAsc') },
          { value: 'category:asc', label: t('expenses.sortCategoryAsc') },
        ]}
      >
        <MonthPickerInput
          label={t('expenses.forMonth')}
          placeholder={t('expenses.allMonths')}
          value={monthToDate(month)}
          onChange={pickMonth}
          valueFormat="YYYY MMMM"
          clearable w={170}
        />
        {/* T11: turlar joriy etilgan bo'lsa TUR bo'yicha, aks holda eski enum bo'yicha filtr */}
        {useTypes ? (
          <Select
            placeholder={t('expenses.allTypes')}
            data={typeList.map((c) => ({
              value: c.id,
              label: c.isActive ? c.name : `${c.name} (${t('expenseTypes.inactive')})`,
            }))}
            value={typeF || null}
            onChange={(v) => { setTypeF(v ?? ''); setPage(1); }}
            clearable searchable w={190}
          />
        ) : (
          <Select
            placeholder={t('expenses.allCategories')}
            data={CATEGORIES.map((c) => ({ value: c, label: catLabel(c) }))}
            value={catF || null}
            onChange={(v) => { setCatF(v ?? ''); setPage(1); }}
            clearable w={170}
          />
        )}
        {/* Xarajat sanasi (spentAt) oralig'i — lokal (Toshkent) "YYYY-MM-DD" yuboriladi */}
        <DatePickerInput
          type="range"
          label={t('att.dateRange')}
          value={range}
          onChange={pickRange}
          valueFormat="DD.MM.YYYY"
          clearable w={230}
        />
        <TableExport<Expense>
          endpoint="/expenses"
          params={filtrlar}
          filename={t('nav.expenses')}
          ustunlar={[
            { key: 'category', label: t('expenses.category') },
            { key: 'amount', label: t('expenses.amount') },
            { key: 'note', label: t('expenses.note') },
            { key: 'spentAt', label: t('expenses.date') },
          ]}
          sanaMaydoni={(r) => r.spentAt}
        />
      </TableToolbar>

      {/* Oy filtri nega bo'shab qolgani — ro'yxat tepasidagi izoh + bir bosishli qaytish */}
      {/* Tor ekranda o'ralsin: `wrap="nowrap"` bilan uzun izoh tugma matnini kesib tashlardi (R36) */}
      {rangeActive && (
        <Group gap="xs" mb="xs" align="center">
          <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
          <Text fz="xs" c="dimmed" style={{ flex: '1 1 240px', minWidth: 0 }}>{t('expenses.rangeMonthHint')}</Text>
          {/* "Barcha oylar" tanlab qo'yilgan bo'lsa qaytadigan oy yo'q — tugma ham ko'rsatilmaydi,
              aks holda yorlig'i ("Oy bo'yicha filtrlash") yolg'on bo'lardi (R35). */}
          {lastMonth ? (
            <Button variant="subtle" size="compact-xs" onClick={clearRange}>{t('expenses.backToMonth')}</Button>
          ) : null}
        </Group>
      )}

      <SectionCard title={t('common.list')} icon={IconCash} p={0} pos="relative">
        {isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : !items.length ? (
          <EmptyState
            icon={IconCash}
            title={narrowFilter ? t('expenses.noMatch') : t('expenses.empty')}
            // Filtrsiz ko'rinishda QAYSI oy bo'sh ekani aytiladi (joriy oy ham, qo'lda tanlangan
            // oy ham) — "umuman xarajat yo'q" degan noto'g'ri xulosa chiqmasin. "Barcha oylar"da
            // esa izoh bo'lmaydi: u yerda sarlavhaning o'zi to'g'ri (R34).
            description={!narrowFilter && month ? t('expenses.emptyMonthHint', { month: monthLabel(month) }) : ''}
          />
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <FetchOverlay visible={fetching} />
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('expenses.category')}</Table.Th>
                  <Table.Th>{t('expenses.staff')}</Table.Th>
                  <Table.Th>{t('expenses.note')}</Table.Th>
                  <Table.Th ta="right">{t('expenses.amount')}</Table.Th>
                  <Table.Th>{t('paymentsPage.method')}</Table.Th>
                  <Table.Th>{t('expenses.date')}</Table.Th>
                  <Table.Th w={44} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((e) => {
                  // T11: ko'rsatiladigan nom — TUR nomi; turga bog'lanmagan eski qatorda
                  // eski enum yorlig'i (rang ham turning guruhidan olinadi).
                  const label = e.categoryRef?.name ?? catLabel(e.category);
                  const color = CAT_COLOR[e.categoryRef?.legacyCategory ?? e.category];
                  return (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Badge color={color} variant="light" radius="sm">{label}</Badge>
                    </Table.Td>
                    <Table.Td>{e.user?.fullName ?? '—'}</Table.Td>
                    <Table.Td><Text size="sm" lineClamp={1} maw={220}>{e.note ?? '—'}</Text></Table.Td>
                    <Table.Td ta="right"><Text fw={700} c="red.7">−{formatSom(e.amount)}</Text></Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{methodLabel(e.method)}</Text></Table.Td>
                    <Table.Td>{formatDate(e.spentAt)}{e.forMonth ? <Text size="xs" c="dimmed">{monthLabel(e.forMonth)}</Text> : null}</Table.Td>
                    <Table.Td>
                      <ThemeIcon variant="subtle" color="red" style={{ cursor: 'pointer' }}
                        onClick={() => confirmDelete(label, () => deleteMut.mutate(e.id))}>
                        <IconTrash size={16} />
                      </ThemeIcon>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      {/* Kassa hisobotidagi bilan AYNI oyna (components/ExpenseFormModal) */}
      <ExpenseFormModal opened={opened} onClose={close} forMonth={month || thisMonth()} />

      {/* Kategoriya / tur detallari — chipga bosilganda ochiladi */}
      <Modal
        opened={!!detailSel}
        onClose={closeDetail}
        size="lg"
        title={
          <Group gap="xs">
            <Badge
              color={detailSel ? (detailSel.kind === 'enum' ? CAT_COLOR[detailSel.value] : detailSel.color) : 'gray'}
              variant="light"
              radius="sm"
            >
              {detailSel ? (detailSel.kind === 'enum' ? catLabel(detailSel.value) : detailSel.name) : ''}
            </Badge>
            <Text fw={700}>{periodLabel}</Text>
          </Group>
        }
      >
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fz="xs" c="dimmed">{t('expenses.categoryTotal')}</Text>
            <Text fw={700} fz="lg" c="red.7">−{formatSom(detailTotal)}</Text>
          </div>
          <div>
            <Text fz="xs" c="dimmed" ta="right">{t('expenses.records')}</Text>
            <Text fw={700} fz="lg" ta="right">{detail.total}</Text>
          </div>
        </Group>

        <Divider my="sm" />

        <TextInput
          placeholder={t('expenses.detailSearch')}
          leftSection={<IconSearch size={16} />}
          value={detailSearch}
          onChange={(e) => setDetailSearch(e.currentTarget.value)}
          mb="sm"
        />

        {detail.isLoading ? (
          <Center h={120}><Text c="dimmed">{t('common.loading')}</Text></Center>
        ) : !detail.items.length ? (
          <Center h={120}><Text c="dimmed">{t('expenses.detailEmpty')}</Text></Center>
        ) : (
          <Box pos="relative">
            <FetchOverlay visible={detail.fetching} />
            <ScrollArea.Autosize mah={360}>
              <Table striped highlightOnHover verticalSpacing="sm" miw={560}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('expenses.date')}</Table.Th>
                    <Table.Th>{t('expenses.staff')}</Table.Th>
                    <Table.Th>{t('expenses.note')}</Table.Th>
                    <Table.Th ta="right">{t('expenses.amount')}</Table.Th>
                    <Table.Th>{t('expenses.createdBy')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {detail.items.map((e) => (
                    <Table.Tr key={e.id}>
                      <Table.Td>
                        <Text fz="sm">{formatDateTime(e.spentAt)}</Text>
                        {e.forMonth ? <Text fz="xs" c="dimmed">{monthLabel(e.forMonth)}</Text> : null}
                      </Table.Td>
                      <Table.Td>{e.user?.fullName ?? '—'}</Table.Td>
                      <Table.Td><Text fz="sm" lineClamp={2} maw={200}>{e.note ?? '—'}</Text></Table.Td>
                      <Table.Td ta="right"><Text fw={700} c="red.7">−{formatSom(e.amount)}</Text></Table.Td>
                      <Table.Td><Text fz="sm" c="dimmed">{e.createdBy?.fullName ?? '—'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </Box>
        )}

        <TablePagination
          page={detail.page}
          pages={detail.pages}
          total={detail.total}
          limit={detail.limit}
          onPage={detail.setPage}
          onLimit={detail.setLimit}
        />
      </Modal>

      {/* T11 — xarajat turlarini boshqarish (faqat administratsiya) */}
      {canEditTypes && (
        <ExpenseCategoriesModal opened={typesOpen} onClose={() => setTypesOpen(false)} items={typeList} />
      )}
    </>
  );
}
